import { initVersionedSchema, toTextContent } from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  Event,
  Interaction,
  TreatmentPlan,
  TreatmentStep,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  EventType,
  IAssociatedTreatment,
  ITag,
  ITreatmentCategory,
  ParticipantType,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  isTag,
  type IEvent,
  type IInteractionV2,
  type IPatient,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  ITimePeriod,
  Timezone,
  WithPrimaryKey,
  getError,
  initFirestoreModel,
  isINamedDocument,
  isSameRef,
  multiMap,
  resolveSequentially,
  snapshotCombineLatest,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientAppointmentDestinationEntity,
  IPatientAppointmentBuildData,
  IPatientAppointmentJobData,
  IPatientAppointmentMigrationData,
  PATIENT_APPOINTMENT_DESTINATION_ENTITY,
} from '../../../destination/entities/patient-appointments';
import {
  IStepWithIdentifiers,
  findTreatmentStepForAppointment,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { getPractitionerOrDefaultMapping } from '../../../mappings/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatientAppointmentDetail,
  CorePracticePatientAppointmentDetailAttendance,
  CorePracticePatientAppointmentDetailStatus,
  ICorePracticePatientAppointmentDetail,
  PatientAppointmentDetailSourceEntity,
} from '../../source/entities/patient-appointment-details';
import {
  CorePracticePatientAppointment,
  PatientAppointmentSourceEntity,
} from '../../source/entities/patient-appointments';
import {
  CorePracticePatientTreatment,
  PatientTreatmentSourceEntity,
} from '../../source/entities/patient-treatments';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { CorePracticeAppointmentBookToPractitionerMappingHandler } from '../mappings/appointment-book-to-practitioner';
import { CorePracticeAppointmentTagMappingHandler } from '../mappings/appointment-tags';
import { CorePracticeEventClassToTreatmentCategoryMappingHandler } from '../mappings/event-class-to-treatment-category';
import { EventClassDestination } from '../mappings/event-class-to-treatment-category-to-xslx';
import { CorePracticeExcludedAppointmentBooksMappingHandler } from '../mappings/exclude-appointment-books';
import { CorePracticePracticeMappingHandler } from '../mappings/practices';
import { CorePracticeStafferMappingHandler } from '../mappings/staff';
import {
  CorePracticeTreatmentPlanDataBuilder,
  PatientTreatmentPlanDestinationEntity,
} from './patient-treatment-plans';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

export const CORE_PRACTICE_MIGRATED_APPOINTMENTS_PLAN_NAME =
  'Core Practice - Migrated Appointments';

interface IJobData extends IPatientAppointmentJobData<CorePracticePatient> {
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  excludedAppointmentBookIds: string[];
  appointmentBookToPractitioner: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  eventClassToTreatmentCategory: WithPrimaryKey<
    ITranslationMap<EventClassDestination>
  >[];
  appointmentTagMappings: WithPrimaryKey<ITranslationMap<ITag>>[];
  appointmentTags: WithRef<ITag>[];
}

export class PatientAppointmentDestinationEntity extends BasePatientAppointmentDestinationEntity<
  CorePracticePatient,
  IJobData
> {
  destinationEntity = PATIENT_APPOINTMENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();
  treatmentPlanName = CORE_PRACTICE_MIGRATED_APPOINTMENTS_PLAN_NAME;

  override canMigrateByDateRange = true;
  override canMigrateByIdRange = true;

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    appointmentDetails: new PatientAppointmentDetailSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
  };

  customMappings = {
    staff: new CorePracticeStafferMappingHandler(),
    practices: new CorePracticePracticeMappingHandler(),
    excludedAppointmentBooks:
      new CorePracticeExcludedAppointmentBooksMappingHandler(),
    appointmentBookToPractitioner:
      new CorePracticeAppointmentBookToPractitionerMappingHandler(),
    eventClassToTreatmentCategory:
      new CorePracticeEventClassToTreatmentCategoryMappingHandler(),
    appointmentTags: new CorePracticeAppointmentTagMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const appointmentBookToPractitioner$ =
      this.customMappings.appointmentBookToPractitioner.getRecords(
        translationMap
      );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const excludedAppointmentBooks$ =
      this.customMappings.excludedAppointmentBooks.getRecords(translationMap);
    const eventClassToTreatmentCategory$ =
      this.customMappings.eventClassToTreatmentCategory.getRecords(
        translationMap
      );
    const appointmentTagMappings$ =
      this.customMappings.appointmentTags.getRecords(translationMap);
    const appointmentTags$ = from(
      Firestore.getDocs(Brand.appointmentTagCol(migration.configuration.brand))
    );

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      brand$,
      practices$,
      from(excludedAppointmentBooks$).pipe(
        multiMap((book) => book.sourceIdentifier.toString())
      ),
      appointmentBookToPractitioner$,
      eventClassToTreatmentCategory$,
      appointmentTags$,
      appointmentTagMappings$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            brand,
            practices,
            excludedAppointmentBookIds,
            appointmentBookToPractitioner,
            eventClassToTreatmentCategory,
            appointmentTags,
            appointmentTagMappings,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            practitioners,
            brand,
            practices,
            excludedAppointmentBookIds,
            appointmentBookToPractitioner,
            eventClassToTreatmentCategory,
            appointmentTags,
            appointmentTagMappings,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IJobData
  ): Promise<
    | IPatientAppointmentMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );

      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `No patient with id ${sourcePatientId}`
        );
      }

      const patient = await Firestore.getDoc(patientRef);

      const sourceAppointments =
        await this.sourceEntities.appointments.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.id
        );

      const sourceTreatments =
        await this.sourceEntities.treatments.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const sourceAppointmentDetails =
        await this.sourceEntities.appointmentDetails.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const planUid = `${sourcePatientId}-appointments`;
      const steps: IStepWithIdentifiers[] = [];

      const appointments: (IPatientAppointmentBuildData | undefined)[] =
        await resolveSequentially(
          sourceAppointments,
          async (sourceAppointment) => {
            const appointmentUid = this.sourceEntities.appointments
              .getSourceRecordId(sourceAppointment.data)
              .toString();

            const appointmentBookId =
              sourceAppointment.data.calendarId.toString();

            const shouldExcludeAppointment =
              data.excludedAppointmentBookIds.includes(appointmentBookId);

            if (shouldExcludeAppointment) {
              return;
            }

            const providerId = sourceAppointment.data.providerId?.toString();

            const practitionerMap = this.resolvePractitioner(
              data,
              appointmentBookId,
              providerId
            );

            if (!practitionerMap) {
              throw new Error(
                `No practitioner with id: ${
                  providerId ?? ''
                }, appointmentBookId: ${appointmentBookId}`
              );
            }

            const practiceSourceId =
              sourceAppointment.data.locationId.toString();
            const practiceMap = data.practices.find(
              (practice) => practice.sourceIdentifier === practiceSourceId
            );

            if (!practiceMap?.destinationIdentifier) {
              throw new Error(`No practice with id ${practiceSourceId}`);
            }

            const practice = await Firestore.getDoc(
              practiceMap.destinationIdentifier
            );

            const createdAt = sourceAppointment.record.translations.from;

            const [appointment, interactions] =
              await this._buildAppointmentData(
                migration,
                translationMap,
                sourcePatientId,
                patient,
                practitionerMap,
                sourceAppointment,
                sourceTreatments,
                sourceAppointmentDetails,
                practice,
                data.appointmentTags,
                data.appointmentTagMappings
              );

            const stepUid = `${sourcePatientId}-${appointmentUid}-appointments`;
            const timezone = migration.configuration.timezone;
            const appointmentDate = toMomentTz(
              sourceAppointment.record.translations.from,
              timezone
            ).format(HISTORY_DATE_FORMAT);

            const mappedCategoryDestination =
              data.eventClassToTreatmentCategory.find(
                (categoryMap) =>
                  categoryMap.sourceIdentifier ===
                  sourceAppointment.data.eventClassId?.toString()
              );
            const categoryDestinationIdentifier = isINamedDocument(
              mappedCategoryDestination?.associatedValue
            )
              ? mappedCategoryDestination?.associatedValue
              : undefined;

            const categoryDestination = categoryDestinationIdentifier
              ? await Firestore.getDoc(categoryDestinationIdentifier.ref)
              : undefined;

            const overrideTreatmentCategory = isTag(categoryDestination)
              ? undefined
              : (categoryDestination?.ref as DocumentReference<ITreatmentCategory>);

            const appointmentTag = isTag(categoryDestination)
              ? (categoryDestination as INamedDocument<ITag>)
              : undefined;
            if (appointmentTag) {
              appointment.tags.push(appointmentTag);
            }

            if (!appointment.treatmentPlan) {
              steps.push({
                ...TreatmentStep.init({
                  name: appointmentDate,
                  status:
                    appointment.status === AppointmentStatus.Complete
                      ? TreatmentStepStatus.Complete
                      : TreatmentStepStatus.Incomplete,
                  schedulingRules: {
                    duration: appointment.event
                      ? Event.duration(appointment.event)
                      : 0,
                  },
                  display: {
                    overrideTreatmentCategory,
                  },
                }),
                sourceIdentifier: stepUid,
                createdAt,
                date: appointmentDate,
              });
            }

            return {
              sourcePatientId: sourcePatientId.toString(),
              patientRef,
              appointmentUid,
              createdAt,
              appointment,
              planUid,
              stepUid,
              interactions,
            };
          }
        );

      return {
        patientRef,
        sourcePatientId: sourcePatientId.toString(),
        appointments: compact(appointments),
        planPairs: [
          {
            plan: {
              ...TreatmentPlan.init({
                name: CORE_PRACTICE_MIGRATED_APPOINTMENTS_PLAN_NAME,
                status: TreatmentPlanStatus.InProgress,
              }),
              sourceIdentifier: planUid,
            },
            steps: uniqBy(steps, (step) => step.sourceIdentifier),
          },
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  resolvePractitioner(
    data: IJobData,
    appointmentBookId: string,
    providerId?: string
  ): DocumentReference<IStaffer> | undefined {
    const appointmentBookPractitionerMap =
      data.appointmentBookToPractitioner.find(
        (practitionerMap) =>
          practitionerMap.sourceIdentifier === appointmentBookId
      );

    if (appointmentBookPractitionerMap?.sourceLink?.sourceIdentifier) {
      const mapping = getPractitionerOrDefaultMapping(
        appointmentBookPractitionerMap.sourceLink.sourceIdentifier,
        data.staff
      )?.destinationIdentifier;
      if (mapping) {
        return mapping;
      }
    }

    return getPractitionerOrDefaultMapping(providerId, data.staff)
      ?.destinationIdentifier;
  }

  private async _buildAppointmentData(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    sourcePatientId: number,
    patient: WithRef<IPatient>,
    practitionerRef: DocumentReference<IStaffer>,
    sourceAppointment: IGetRecordResponse<CorePracticePatientAppointment>,
    sourceTreatments: IGetRecordResponse<CorePracticePatientTreatment>[],
    sourceAppointmentDetails: IGetRecordResponse<CorePracticePatientAppointmentDetail>[],
    practice: WithRef<IPractice>,
    appointmentTags: WithRef<ITag>[],
    appointmentTagMappings: WithPrimaryKey<ITranslationMap<ITag>>[]
  ): Promise<[IPatientAppointmentBuildData['appointment'], IInteractionV2[]]> {
    const practitioner = await Firestore.getDoc(practitionerRef);

    const interactions = buildInteractions(sourceAppointment);

    const appointmentStartTime = toMomentTz(
      sourceAppointment.record.translations.from,
      practice.settings.timezone
    );
    const appointmentEndTime = toMomentTz(
      sourceAppointment.record.translations.to,
      practice.settings.timezone
    );

    const treatments = sourceTreatments.filter((treatment) =>
      treatment.record.translations.completeDate
        ? toMomentTz(
            treatment.record.translations.completeDate,
            practice.settings.timezone
          ).isSame(appointmentStartTime, 'day')
        : false
    );

    const appointmentDetails = findAppointmentDetails(
      sourceAppointmentDetails,
      sourceAppointment,
      appointmentStartTime,
      appointmentEndTime,
      practice.settings.timezone
    );

    const associatedPlan = await resolveAssociatedPlan(
      sourcePatientId,
      practice,
      translationMap,
      treatments
    );

    const tags = compact(
      (appointmentDetails?.data.tags || [])
        .map(
          (sourceTag) =>
            appointmentTagMappings.find(
              (tag) => tag.sourceIdentifier === sourceTag
            )?.destinationIdentifier
        )
        .map((tagMapping) =>
          appointmentTags.find((tag) => isSameRef(tag, tagMapping))
        )
        .map((tag) => (tag ? toNamedDocument(tag) : undefined))
    );

    const appointment: IPatientAppointmentBuildData['appointment'] = {
      event: getAppointmentEvent(
        {
          from: appointmentStartTime,
          to: appointmentEndTime,
        },
        patient,
        practice,
        practitioner
      ),
      eventHistory: [],
      status: determineAppointmentStatus(
        sourceAppointment,
        appointmentStartTime,
        migration,
        appointmentDetails?.data
      ),
      statusHistory: [],
      practice: toNamedDocument(practice),
      practitioner: stafferToNamedDoc(practitioner),
      cancellationHistory: [],
      dependencies: [],
      tags,
      ...initFirestoreModel(),
      deleted: sourceAppointment.data.isDeleted,
      treatmentPlan: associatedPlan,
    };

    return [appointment, interactions];
  }
}

function buildInteractions(
  sourceAppointment: IGetRecordResponse<CorePracticePatientAppointment>
): IInteractionV2[] {
  const createdAt = toMoment(
    sourceAppointment.record.translations.from
  ).isAfter(moment())
    ? toTimestamp()
    : sourceAppointment.record.translations.from;

  return compact([
    sourceAppointment.data.description,
    sourceAppointment.data.note,
  ]).map((note) => {
    return Interaction.init({
      uid: sourceAppointment.record.uid,
      title: [toTextContent(`Added a note`)],
      content: initVersionedSchema(note),
      createdAt,
      pinned: true,
    });
  });
}

function findAppointmentDetails(
  sourceAppointmentDetails: IGetRecordResponse<CorePracticePatientAppointmentDetail>[],
  sourceAppointment: IGetRecordResponse<CorePracticePatientAppointment>,
  appointmentStartTime: moment.Moment,
  appointmentEndTime: moment.Moment,
  timezone: Timezone
): IGetRecordResponse<CorePracticePatientAppointmentDetail> | undefined {
  return sourceAppointmentDetails.find((detail) => {
    if (detail.data.calendarId !== sourceAppointment.data.calendarId) {
      return false;
    }

    if (
      !toMomentTz(detail.data.startTime, timezone).isSame(
        appointmentStartTime,
        'minute'
      )
    ) {
      return false;
    }

    if (
      !toMomentTz(detail.data.endTime, timezone).isSame(
        appointmentEndTime,
        'minute'
      )
    ) {
      return false;
    }

    return true;
  });
}

function determineAppointmentStatus(
  sourceAppointment: IGetRecordResponse<CorePracticePatientAppointment>,
  appointmentStartTime: moment.Moment,
  migration: WithRef<IPracticeMigration>,
  appointmentDetails?: ICorePracticePatientAppointmentDetail
): AppointmentStatus {
  const status = appointmentDetails?.status;
  const attendance = appointmentDetails?.attendance;

  if (!status) {
    return sourceAppointment.data.cancelDate
      ? AppointmentStatus.Cancelled
      : appointmentStartTime.isAfter(
            toMoment(migration.configuration.backupDate)
          )
        ? AppointmentStatus.Scheduled
        : AppointmentStatus.Complete;
  }

  if (
    appointmentStartTime.isAfter(toMoment(migration.configuration.backupDate))
  ) {
    switch (status) {
      case CorePracticePatientAppointmentDetailStatus.Cancelled:
        return AppointmentStatus.Cancelled;
      case CorePracticePatientAppointmentDetailStatus.Confirmed:
        return AppointmentStatus.Confirmed;
      default:
        return AppointmentStatus.Scheduled;
    }
  }

  switch (status) {
    case CorePracticePatientAppointmentDetailStatus.Tentative:
      if (
        attendance === CorePracticePatientAppointmentDetailAttendance.Attended
      ) {
        return AppointmentStatus.Complete;
      }
      return AppointmentStatus.Scheduled;
    case CorePracticePatientAppointmentDetailStatus.Confirmed:
      if (
        attendance === CorePracticePatientAppointmentDetailAttendance.Attended
      ) {
        return AppointmentStatus.Complete;
      }
      return AppointmentStatus.Confirmed;
    case CorePracticePatientAppointmentDetailStatus.Cancelled:
      return AppointmentStatus.Cancelled;
    default:
      return AppointmentStatus.Scheduled;
  }
}

export function getAppointmentEvent(
  period: ITimePeriod,
  patient: WithRef<IPatient>,
  practice: WithRef<IPractice>,
  practitioner: INamedDocument<IStaffer> | WithRef<IStaffer>
): IEvent | undefined {
  if (!practice || !patient || !practitioner) {
    return;
  }
  return Event.init({
    from: toTimestamp(period.from),
    to: toTimestamp(period.to),
    practice: toNamedDocument(practice),
    type: EventType.Appointment,
    participants: [
      {
        ...toNamedDocument(patient),
        type: ParticipantType.Patient,
      },
      {
        ...stafferToNamedDoc(practitioner),
        type: ParticipantType.Staffer,
      },
    ],
  });
}

async function resolveAssociatedPlan(
  sourcePatientId: number,
  practice: WithRef<IPractice>,
  translationMap: TranslationMapHandler,
  treatments: IGetRecordResponse<CorePracticePatientTreatment>[]
): Promise<IAssociatedTreatment | undefined> {
  const stepUid = CorePracticeTreatmentPlanDataBuilder.getStepIdentifier(
    sourcePatientId,
    practice.settings.timezone,
    treatments
  );

  if (!stepUid) {
    return;
  }

  const stepRef = await findTreatmentStepForAppointment(
    translationMap,
    stepUid
  );

  if (!stepRef) {
    return;
  }

  const step = await Firestore.safeGetDoc(stepRef);
  if (!step) {
    return;
  }

  const plan = await TreatmentStep.treatmentPlan(step);

  return TreatmentPlan.treatmentStepToAssociatedTreatment(plan, step);
}
